import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { smsTemplatesApi } from '../services/api'
import { showError } from '../utils/showError'
import { extractErrorMessage } from '../utils/errorUtils'
import { normalizeVariable } from '../utils/templateVariableHelpers'
import {
  LoadingSpinner,
  PageHeader,
  Button,
  FormField,
  FormSelect,
  FormTextarea,
  Card,
  CardHeader,
  CardBody,
  ErrorMessage,
  Modal
} from '../components/common'

const EditSmsTemplate = () => {
  const { templateType } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preselectedType = !templateType ? searchParams.get('type') : null
  const [formData, setFormData] = useState({
    templateType: 0,
    templateName: '',
    messageBody: ''
  })
  const [variables, setVariables] = useState([])
  const [templateTypes, setTemplateTypes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showVariables, setShowVariables] = useState(true)
  const [initialLoading, setInitialLoading] = useState(true)
  const [showAIModal, setShowAIModal] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState(null)

  useEffect(() => {
    loadTemplateTypes()
    if (templateType) {
      loadTemplateDetails()
    } else if (preselectedType) {
      const typeVal = parseInt(preselectedType)
      setFormData(prev => ({ ...prev, templateType: typeVal }))
      loadTemplateVariables(typeVal)
      setInitialLoading(false)
    } else {
      setInitialLoading(false)
    }
  }, [templateType])

  const loadTemplateTypes = async () => {
    try {
      const response = await smsTemplatesApi.getTypes()
      setTemplateTypes(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      console.error('Failed to load template types:', err)
      setTemplateTypes([])
    }
  }

  const loadTemplateDetails = async () => {
    try {
      setInitialLoading(true)
      const response = await smsTemplatesApi.get(templateType)
      const templateData = {
        templateType: parseInt(templateType),
        templateName: response.data.templateName || '',
        messageBody: response.data.messageBody || ''
      }
      setFormData(templateData)
      loadTemplateVariables(parseInt(templateType))
    } catch (err) {
      console.error('Failed to load template details:', err)
      setError(extractErrorMessage(err))
    } finally {
      setInitialLoading(false)
    }
  }

  const loadTemplateVariables = async (templateTypeValue) => {
    try {
      console.log('Loading variables for template type:', templateTypeValue)
      const response = await smsTemplatesApi.getVariablesByType(templateTypeValue)
      console.log('Variables API response:', response)
      console.log('Variables data:', response.data)
      
      // The API returns {templateType: 0, variables: [{name, isRequired, description}, ...]}
      // We store the full objects to have access to all properties
      if (response.data && Array.isArray(response.data.variables)) {
        setVariables(response.data.variables)
      } else if (Array.isArray(response.data)) {
        setVariables(response.data)
      } else {
        setVariables([])
      }
    } catch (err) {
      console.error('Failed to load template variables:', err)
      console.error('Error response:', err.response)
      setVariables([])
    }
  }

  const getTemplateTypeName = (value) => {
    const type = templateTypes.find(t => t.value === parseInt(value))
    return type ? type.name : `Type ${value}`
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'templateType' ? parseInt(value) : value
    }))
  }

  const handleMessageBodyChange = (e) => {
    setFormData(prev => ({
      ...prev,
      messageBody: e.target.value
    }))
  }

  const handleTemplateTypeChange = (e) => {
    const newType = parseInt(e.target.value)
    setFormData(prev => ({
      ...prev,
      templateType: newType
    }))
    loadTemplateVariables(newType)
  }

  const insertVariable = (variable) => {
    // If variable is an object, use its name property
    const variableName = typeof variable === 'object' ? variable.name : variable
    const placeholder = `{{${variableName}}}`
    setFormData(prev => ({
      ...prev,
      messageBody: prev.messageBody + placeholder
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const apiData = {
        templateType: formData.templateType,
        templateName: formData.templateName,
        messageBody: formData.messageBody
      }

      if (templateType) {
        await smsTemplatesApi.update(apiData)
      } else {
        await smsTemplatesApi.create(apiData)
      }

      navigate('/sms-templates')
    } catch (err) {
      showError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      setAiError('Please enter a description for the AI to generate content')
      return
    }

    setAiLoading(true)
    setAiError(null)

    try {
      const aiRequest = {
        prompt: aiPrompt,
        templateType: formData.templateType,
        currentMessage: formData.messageBody || undefined
      }

      const response = await smsTemplatesApi.generateAI(aiRequest)
      
      // Update form with AI-generated content
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          messageBody: response.data.messageBody || prev.messageBody
        }))
        setShowAIModal(false)
        setAiPrompt('')
      }
    } catch (err) {
      setAiError(extractErrorMessage(err))
    } finally {
      setAiLoading(false)
    }
  }

  const renderAIModal = () => {
    if (!showAIModal) return null

    return (
      <Modal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        title="Generate SMS Template with AI"
        size="large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-4">
          {aiError && (
            <ErrorMessage 
              message={aiError}
              onDismiss={() => setAiError(null)}
            />
          )}
          
          <FormTextarea
            label="Describe the SMS template you want"
            name="aiPrompt"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            rows={4}
            placeholder="E.g., Create a welcome SMS for new building residents that includes their unit number and building name..."
            disabled={aiLoading}
          />
          
          {formData.templateType && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700">
                <strong>Template Type:</strong> {getTemplateTypeName(formData.templateType)}
              </p>
              {formData.messageBody && (
                <p className="text-xs text-blue-600 mt-1">
                  The AI will improve upon your existing content.
                </p>
              )}
            </div>
          )}
          
          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowAIModal(false)
                setAiPrompt('')
                setAiError(null)
              }}
              disabled={aiLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAIGenerate();
              }}
              disabled={aiLoading || !aiPrompt.trim()}
              loading={aiLoading}
            >
              Generate
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  if (initialLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className="flex flex-col h-full space-y-6">
      <PageHeader 
        title={templateType ? `Edit ${getTemplateTypeName(templateType)}` : 'Create New SMS Template'}
        subtitle="Design and customize your SMS template"
        backLink={{
          label: "Back to Templates",
          href: "/sms-templates"
        }}
      />

      {error && (
        <ErrorMessage 
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex gap-6">
          {/* Main Form */}
          <Card className="flex-1 overflow-y-auto">
            <CardBody>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <FormSelect
                    label="Template Type"
                    name="templateType"
                    value={formData.templateType}
                    onChange={handleTemplateTypeChange}
                    options={[
                      { value: '', label: 'Select a template type' },
                      ...templateTypes.map(type => ({ value: type.value, label: type.name }))
                    ]}
                    required
                    disabled={!!templateType}
                  />

                  <FormField
                    label="Template Name"
                    name="templateName"
                    value={formData.templateName}
                    onChange={handleInputChange}
                    required
                    maxLength={100}
                  />
                </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Message Body
                  </label>
                  <Button
                    variant="primary"
                    size="small"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowAIModal(true);
                    }}
                    disabled={formData.templateType === null || formData.templateType === undefined || formData.templateType === ''}
                    title={(formData.templateType === null || formData.templateType === undefined || formData.templateType === '') ? "Select a template type first" : "Generate content with AI"}
                    className="bg-purple-600 hover:bg-purple-700 text-white border-0"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    AI Generate
                  </Button>
                </div>
                
                <FormTextarea
                  name="messageBody"
                  value={formData.messageBody}
                  onChange={handleMessageBodyChange}
                  rows={8}
                  required
                  maxLength={1000}
                  placeholder="Enter your SMS message here..."
                />
                
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Character count: {formData.messageBody?.length || 0}/1000</span>
                  <span className={formData.messageBody?.length > 160 ? 'text-orange-600' : 'text-gray-500'}>
                    {formData.messageBody?.length <= 160 ? '1 SMS' : `${Math.ceil(formData.messageBody?.length / 160)} SMS messages`}
                  </span>
                </div>
              </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate('/sms-templates');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    loading={loading}
                  >
                    Save Template
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>

          {/* Variables Sidebar */}
          <Card className={`transition-all duration-300 ${showVariables ? 'w-80' : 'w-12'}`}>
            <div className="h-full flex flex-col">
              <CardHeader>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowVariables(!showVariables);
                  }}
                  className="w-full justify-between"
                >
                  {showVariables ? (
                    <>
                      <span className="text-lg font-semibold">Template Variables</span>
                      <span className="text-gray-400">›</span>
                    </>
                  ) : (
                    <span className="text-gray-400 mx-auto">‹</span>
                  )}
                </Button>
              </CardHeader>
              
              {showVariables && (
                <CardBody className="flex-1 overflow-y-auto">
                  <p className="text-xs text-gray-600 mb-4">
                    Type: {getTemplateTypeName(formData.templateType)}
                  </p>
                  
                  {variables.length === 0 ? (
                    <div>
                      <p className="text-gray-500 text-sm mb-2">No variables available for this template type.</p>
                      <p className="text-xs text-gray-400 mt-2">Some template types may not have predefined variables.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 mb-3">
                        Click on a variable to insert it into the message:
                      </p>
                      {variables.some(v => v.isRequired) && (
                        <p className="text-xs text-red-500 mb-2">* Required variables</p>
                      )}
                      {variables.map((variable, index) => {
                        const { name: variableName, isRequired, description, type: variableType } = normalizeVariable(variable)

                        return (
                          <div key={index} className="mb-2">
                            <Button
                              variant="ghost"
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                insertVariable(variable);
                              }}
                              className="w-full justify-start p-2 bg-gray-50 border border-gray-200 rounded-md hover:bg-primary-50 hover:border-primary-300 text-sm font-mono break-all"
                            >
                              <div className="flex items-center justify-between w-full">
                                <span>{`{{${variableName}}}`}</span>
                                <span className="flex items-center">
                                  {isRequired && (
                                    <span className="text-xs text-red-500 ml-2">*</span>
                                  )}
                                  {variableType && variableType !== 'Text' && (
                                    <span className="text-[10px] text-blue-500 ml-1 font-normal">{variableType.toLowerCase()}</span>
                                  )}
                                </span>
                              </div>
                            </Button>
                            {description && (
                              <p className="text-xs text-gray-500 mt-1 ml-2">{description}</p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">SMS Tips:</h4>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Keep messages concise and clear</li>
                      <li>• 160 characters = 1 SMS message</li>
                      <li>• Longer messages will be split</li>
                      <li>• Click variables to insert them</li>
                    </ul>
                  </div>

                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <h4 className="text-sm font-semibold text-yellow-800 mb-2">Variable Usage:</h4>
                    <ul className="text-xs text-yellow-700 space-y-1">
                      <li>• Variables are case-sensitive</li>
                      <li>• Use double curly braces: {`{{variable}}`}</li>
                      <li>• Required variables must be included</li>
                      <li>• Variables will be replaced when SMS is sent</li>
                    </ul>
                  </div>

                  {/* Debug info - check browser console */}
                  <details className="mt-4">
                    <summary className="text-xs text-gray-500 cursor-pointer">Debug Info</summary>
                    <div className="text-xs bg-gray-100 p-2 mt-2 rounded overflow-auto">
                      <p>Template Type: {formData.templateType}</p>
                      <p>Variables Count: {variables.length}</p>
                      <Button
                        variant="ghost"
                        size="small"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          loadTemplateVariables(formData.templateType);
                        }}
                        className="text-primary-600 text-xs underline mt-2"
                      >
                        Retry loading variables
                      </Button>
                    </div>
                  </details>
                </CardBody>
              )}
            </div>
          </Card>
        </div>
      </div>
      
      {renderAIModal()}
    </div>
  )
}

export default EditSmsTemplate